import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    HStack, FormControl, FormLabel, Input, NumberInput, NumberInputField,
    Select, SimpleGrid, Text, VStack, Checkbox, Box, Button,
} from '@chakra-ui/react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { useLayoutStore, PAPER_SIZES } from '../../state/useLayoutStore';
import {
    applyDefaultPageSetup, layoutPageSetup, naturalPaperId, patchPageSetup,
    STANDARD_SCALES, paperSizeMm,
} from '../../services/plot/pageSetup';
import type { PageSetupDialogRequest } from '../../services/plot/plotDialogBridge';
import type { PageSetup } from '../../services/plot/types';
import { notify } from '../../utils/notify';
import i18n from '../../i18n';

interface PageSetupDialogProps {
    isOpen: boolean;
    request: PageSetupDialogRequest | null;
    onClose: () => void;
}

/**
 * PAGESETUP dialog (Faz 2 task 2.7): edits the named page setup of one
 * layout — paper + orientation, fit/scale, offset, plot type, plot style
 * name — and stores it on the layout (persisted through the .yap cadScene
 * envelope). "New from current" clones the working state under a new name,
 * mirroring OCS's named-setup list.
 */
export function PageSetupDialog({ isOpen, request, onClose }: PageSetupDialogProps) {
    const { t } = useTranslation();
    const layouts = useLayoutStore((s) => s.layouts);
    const setPageSetup = useLayoutStore((s) => s.setPageSetup);

    const [layoutId, setLayoutId] = useState<string | null>(null);
    const [setup, setSetup] = useState<PageSetup>(() => applyDefaultPageSetup());

    useEffect(() => {
        if (!isOpen) return;
        const store = useLayoutStore.getState();
        const lid = request?.layoutId ?? store.activeId;
        setLayoutId(lid);
        const layout = store.layouts.find((l) => l.id === lid);
        setSetup(layout ? { ...layoutPageSetup(layout) } : applyDefaultPageSetup());
    }, [isOpen, request]);

    const patch = (p: Partial<PageSetup>) => setSetup((s) => patchPageSetup(s, p));
    const paperId = naturalPaperId(setup.paperWidth, setup.paperHeight) ?? 'A4';

    const apply = () => {
        if (!layoutId) {
            notify(i18n.t('tools.notify.pageSetupNeedsLayout'), 'warning');
            return;
        }
        setPageSetup(layoutId, setup);
        notify(i18n.t('tools.notify.pageSetupSaved', { name: setup.name }), 'success');
        onClose();
    };

    const newFromCurrent = () => {
        const n = layouts.length + 1;
        patch({ name: `Setup ${n}` });
        notify(i18n.t('tools.notify.pageSetupCloned', { name: `Setup ${n}` }), 'info');
    };

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            title={t('dialogs.ui.pageSetup.title')}
            footer={
                <>
                    <DialogButton variant="ghost" onClick={onClose}>{t('dialogs.ui.cancel')}</DialogButton>
                    <DialogButton variant="secondary" onClick={newFromCurrent}>
                        {t('dialogs.ui.pageSetup.newFromCurrent')}
                    </DialogButton>
                    <DialogButton variant="primary" onClick={apply}>
                        {t('dialogs.ui.pageSetup.apply')}
                    </DialogButton>
                </>
            }
        >
            <VStack align="stretch" spacing={3}>
                <SimpleGrid columns={2} spacing={3}>
                    <FormControl>
                        <FormLabel fontSize="xs">{t('dialogs.ui.pageSetup.layout')}</FormLabel>
                        <Select
                            size="sm"
                            value={layoutId ?? ''}
                            onChange={(e) => {
                                const id = e.target.value || null;
                                setLayoutId(id);
                                const layout = layouts.find((l) => l.id === id);
                                setSetup(layout ? { ...layoutPageSetup(layout) } : applyDefaultPageSetup());
                            }}
                        >
                            <option value="">{t('dialogs.ui.pageSetup.noLayout')}</option>
                            {layouts.map((l) => (
                                <option key={l.id} value={l.id}>{l.name}</option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs">{t('dialogs.ui.pageSetup.name')}</FormLabel>
                        <Input
                            size="sm"
                            value={setup.name}
                            onChange={(e) => patch({ name: e.target.value })}
                        />
                    </FormControl>
                </SimpleGrid>

                <SimpleGrid columns={2} spacing={3}>
                    <FormControl>
                        <FormLabel fontSize="xs">{t('dialogs.ui.pageSetup.paper')}</FormLabel>
                        <Select
                            size="sm"
                            value={paperId}
                            onChange={(e) => {
                                const mm = paperSizeMm(e.target.value);
                                const landscape = setup.paperWidth >= setup.paperHeight;
                                patch({
                                    paperName: `${PAPER_SIZES[e.target.value]?.label ?? e.target.value} (${Math.max(mm.w, mm.h).toFixed(2)} x ${Math.min(mm.w, mm.h).toFixed(2)} mm)`,
                                    paperWidth: landscape ? mm.w : mm.h,
                                    paperHeight: landscape ? mm.h : mm.w,
                                });
                            }}
                        >
                            {Object.entries(PAPER_SIZES).map(([id, size]) => (
                                <option key={id} value={id}>{size.label}</option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs">{t('dialogs.ui.pageSetup.plotType')}</FormLabel>
                        <Select
                            size="sm"
                            value={setup.plotType}
                            onChange={(e) => patch({ plotType: e.target.value as PageSetup['plotType'] })}
                        >
                            <option value="layout">{t('dialogs.ui.pageSetup.typeLayout')}</option>
                            <option value="extents">{t('dialogs.ui.pageSetup.typeExtents')}</option>
                            <option value="display">{t('dialogs.ui.pageSetup.typeDisplay')}</option>
                            <option value="window">{t('dialogs.ui.pageSetup.typeWindow')}</option>
                        </Select>
                    </FormControl>
                </SimpleGrid>

                <HStack spacing={4}>
                    <Checkbox
                        size="sm"
                        isChecked={setup.paperWidth >= setup.paperHeight}
                        onChange={(e) => {
                            const landscape = e.target.checked;
                            const w = Math.max(setup.paperWidth, setup.paperHeight);
                            const h = Math.min(setup.paperWidth, setup.paperHeight);
                            patch({ paperWidth: landscape ? w : h, paperHeight: landscape ? h : w });
                        }}
                    >
                        <Text fontSize="xs">{t('dialogs.ui.plot.landscape')}</Text>
                    </Checkbox>
                    <Checkbox
                        size="sm"
                        isChecked={setup.rotation === 180 || setup.rotation === 90}
                        onChange={(e) => {
                            const base = (setup.paperWidth >= setup.paperHeight) ? 0 : 90;
                            patch({ rotation: e.target.checked ? ((base === 0 ? 180 : 270) as PageSetup['rotation']) : (base as PageSetup['rotation']) });
                        }}
                    >
                        <Text fontSize="xs">{t('dialogs.ui.plot.upsideDown')}</Text>
                    </Checkbox>
                </HStack>

                <SimpleGrid columns={3} spacing={3}>
                    <FormControl>
                        <FormLabel fontSize="xs">{t('dialogs.ui.plot.scale')}</FormLabel>
                        <Select
                            size="sm"
                            value={setup.fitToPaper ? 'fit' : `${setup.scaleNum}/${setup.scaleDen}`}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === 'fit') { patch({ fitToPaper: true }); return; }
                                const [n, d] = v.split('/').map(Number);
                                patch({ fitToPaper: false, scaleNum: n, scaleDen: d });
                            }}
                        >
                            <option value="fit">{t('dialogs.ui.plot.fitToPaper')}</option>
                            {STANDARD_SCALES.map((s) => (
                                <option key={`${s.num}/${s.den}`} value={`${s.num}/${s.den}`}>
                                    {`${s.num}:${s.den}`}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs">{t('dialogs.ui.pageSetup.offsetX')}</FormLabel>
                        <NumberInput size="sm" value={setup.offsetX}
                            onChange={(_, v) => patch({ offsetX: Number.isFinite(v) ? v : 0 })}>
                            <NumberInputField />
                        </NumberInput>
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="xs">{t('dialogs.ui.pageSetup.offsetY')}</FormLabel>
                        <NumberInput size="sm" value={setup.offsetY}
                            onChange={(_, v) => patch({ offsetY: Number.isFinite(v) ? v : 0 })}>
                            <NumberInputField />
                        </NumberInput>
                    </FormControl>
                </SimpleGrid>

                <Checkbox
                    size="sm"
                    isChecked={setup.centerPlot}
                    onChange={(e) => patch({ centerPlot: e.target.checked })}
                >
                    <Text fontSize="xs">{t('dialogs.ui.plot.centerPlot')}</Text>
                </Checkbox>

                <Text fontSize="2xs" color="text.secondary">
                    {t('dialogs.ui.pageSetup.persistHint')}
                </Text>
                <Box minH="1" />
            </VStack>
        </AppDialog>
    );
}

/** Lazy-mount gate shared with ProjectDialogsHost. */
export function isPageSetupDialogEvent(e: Event): e is CustomEvent<PageSetupDialogRequest> {
    return e.type === 'yap:open-pagesetup-dialog';
}
